import { playerSearchWithMetaRatings } from "@/api/api";
import { IncludeExcludeToggle } from "@/components/IncludeExcludeToggle";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import Input from "@/design-system/Input/Input";
import PlayerCardCondensed from "@/design-system/PlayerCard/PlayerCardCondensed";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { getUserSelector } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { type LaneKind, PLAYER_SEARCH } from "@repo/shared/utils";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const CARD_SCALE = 0.62;

const VISIBLE_PICKS = 3;

const PlayerPickCard = ({
  player,
  lane,
  onPick,
  onRemove,
}: {
  player: SearchedPlayer;
  lane: LaneKind | undefined;
  onPick: (lane: LaneKind) => void;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation("sbcs");
  const clickable = !onRemove;

  return (
    <div className="relative">
      <Card
        hoverable={clickable}
        className="flex h-full flex-col overflow-hidden p-0"
      >
        <div
          className={cn(
            "flex flex-1 justify-center p-3",
            clickable && "cursor-pointer",
          )}
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? () => onPick("include") : undefined}
          onKeyDown={
            clickable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onPick("include");
                }
              : undefined
          }
        >
          <PlayerCardCondensed player={player} scale={CARD_SCALE} hidePrice />
        </div>
        <IncludeExcludeToggle
          variant="footer"
          value={lane}
          onChange={(value) =>
            onPick(value === "exclude" ? "exclude" : "include")
          }
        />
      </Card>
      {onRemove && (
        <CloseButton
          onClick={onRemove}
          title={t("customize.removePlayer", { name: player.cardName })}
          className="absolute right-2 top-2"
        />
      )}
    </div>
  );
};

const CardGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-3">
    {children}
  </div>
);

export const PlayerSearch = ({
  included,
  excluded,
  onAdd,
  onRemove,
  onFocusGate,
}: {
  included: SearchedPlayer[];
  excluded: SearchedPlayer[];
  onAdd: (lane: LaneKind, player: SearchedPlayer) => void;
  onRemove: (lane: LaneKind, resourceId: number) => void;
  onFocusGate?: () => boolean;
}) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const { user } = useSelector(getUserSelector);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<SearchedPlayer[]>([]);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const query = term.trim();
  const searchActive = query.length >= PLAYER_SEARCH.minChars;

  const { debouncedSearchTerm } = useDebounce(query, {
    delay: PLAYER_SEARCH.debounceMs,
  });

  useEffect(() => {
    if (debouncedSearchTerm.length < PLAYER_SEARCH.minChars) return;
    let cancelled = false;
    const search = async () => {
      setSearching(true);
      try {
        const res = await playerSearchWithMetaRatings(
          debouncedSearchTerm,
          1,
          user.platform || "Playstation",
        );
        if (!cancelled) setResults(res.data ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    };
    search();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm, user.platform]);

  const openSearch = () => {
    if (onFocusGate?.()) return;
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setTerm("");
    setResults([]);
  };

  const [order, setOrder] = useState<number[]>(() =>
    [...included, ...excluded].map((p) => p.resourceId),
  );
  const remember = (resourceId: number) =>
    setOrder((ids) => (ids.includes(resourceId) ? ids : [...ids, resourceId]));

  const pick = (lane: LaneKind, player: SearchedPlayer) => {
    remember(player.resourceId);
    onAdd(lane, player);
    closeSearch();
  };

  const rank = (resourceId: number) => {
    const i = order.indexOf(resourceId);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const picks: { player: SearchedPlayer; lane: LaneKind }[] = [
    ...included.map((player) => ({ player, lane: "include" as const })),
    ...excluded.map((player) => ({ player, lane: "exclude" as const })),
  ].sort((a, b) => rank(a.player.resourceId) - rank(b.player.resourceId));

  const laneOf = (resourceId: number) =>
    picks.find((p) => p.player.resourceId === resourceId)?.lane;

  const resultsShowing =
    searchOpen && searchActive && !searching && results.length > 0;

  const collapsible = picks.length > VISIBLE_PICKS + 1;
  const shown =
    collapsible && !expanded ? picks.slice(0, VISIBLE_PICKS) : picks;
  const hidden = picks.length - shown.length;

  return (
    <div className="flex min-w-0 flex-col gap-4 px-4 pb-4">
      {searchOpen ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Input
              size="small"
              autoFocus
              value={term}
              onChange={setTerm}
              placeholder={t("customize.searchPlayers")}
            />
            <CloseButton onClick={closeSearch} />
          </div>

          {searchActive &&
            (searching ? (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            ) : results.length === 0 ? (
              <p className="text-xs text-foreground-muted">
                {t("customize.noPlayersNamed", { name: query })}
              </p>
            ) : (
              <ScrollArea className="pr-1">
                <div className="max-h-96">
                  <CardGrid>
                    {results.slice(0, PLAYER_SEARCH.maxResults).map((p) => (
                      <PlayerPickCard
                        key={p.resourceId}
                        player={p}
                        lane={laneOf(p.resourceId)}
                        onPick={(lane) => pick(lane, p)}
                      />
                    ))}
                  </CardGrid>
                </div>
              </ScrollArea>
            ))}
        </div>
      ) : (
        <Button
          variant="secondary"
          size="small"
          icon={<Plus />}
          onClick={openSearch}
          className="self-start"
        >
          {t("common:addPlayer")}
        </Button>
      )}

      {!resultsShowing && picks.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="text-xs font-medium text-foreground-muted">
            {t("customize.playerCount", { count: picks.length })}
          </span>
          <CardGrid>
            {shown.map(({ player, lane }) => (
              <PlayerPickCard
                key={player.resourceId}
                player={player}
                lane={lane}
                onPick={(next) => {
                  if (next !== lane) onAdd(next, player);
                }}
                onRemove={() => onRemove(lane, player.resourceId)}
              />
            ))}
            {hidden > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="flex flex-col items-center justify-center gap-1 rounded-[20px] border border-dashed border-neutral-600 p-3 transition-colors hover:border-neutral-500 hover:bg-neutral-750"
              >
                <span className="text-h4 font-bold text-neutral-100">
                  +{hidden}
                </span>
                <span className="text-small font-semibold text-foreground-muted">
                  {t("common:showAll")}
                </span>
              </button>
            )}
          </CardGrid>
          {collapsible && expanded && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="self-start text-xs font-semibold text-foreground-muted transition-colors hover:text-white"
            >
              {t("common:showLess")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
